// TabHandle — THE navpanel / inspector tab handle (DESIGN-SYSTEM.md §2).
//
// One icon-only `role="tab"` button, used by every rail in the product: the
// workspace nav rail (Board / Types / Process / Views / Docs / Problems), its
// mobile bottom strip, and the inspector rail. Before this existed each rail
// hand-assembled its own button, so they drifted apart on radius, active paint,
// badge shape and focus ring (the nav rail had no focus ring at all).
//
// States: default · hover · active · focus-visible · disabled. There is no
// "empty" state — an empty tab is a NORMAL handle; emptiness is communicated by
// the panel body's empty state, never by fading the handle. `disabled` is
// reserved for genuinely unavailable tabs (no permission, not loaded yet).
//
// Per-handle complications plug in as DATA, not as a subclass: `badge` (a count
// or a dot, tinted by tone) and `overlay` (an escape-hatch corner node). That
// is how the Problems handle carries its error/warning/todo count without
// forking the base handle.
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Badge, type BadgeTone } from "./Badge.js";
import { cx } from "./classNames.js";

export type TabHandleSize = "sm" | "md" | "touch";

/** Badge tint. Mirrors diagnostic severity plus a neutral rung for counts that
 *  carry no severity (e.g. a review tab's comment count). */
export type TabBadgeTone = BadgeTone;

/** A count pill, or a bare dot when the magnitude is unknown/uninteresting. */
export type TabHandleBadge =
  | { readonly count: number; readonly tone: TabBadgeTone }
  | { readonly dot: TabBadgeTone };

const SIZE_CLASSES: Record<TabHandleSize, string> = {
  // Compact desktop rails.
  sm: "h-7 w-7 rounded",
  md: "h-8 w-8 rounded-md",
  // The phone bottom strip, styled as a segmented glass pill.
  touch: "h-11 flex-1 rounded-xl active:scale-95",
};

const IDLE_CLASSES: Record<TabHandleSize, string> = {
  sm: "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200",
  md: "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200",
  touch: "text-slate-500 hover:text-slate-300",
};

const ACTIVE_CLASSES: Record<TabHandleSize, string> = {
  sm: "bg-slate-800 text-slate-100",
  md: "bg-slate-800 text-slate-100",
  touch: "bg-white/10 text-arch-300",
};

export interface TabHandleProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "type"> {
  /** Accessible name (there is no text child to name it). Also the tooltip
   *  content when the call site wraps this in a `<Tooltip>`. */
  readonly label: string;
  readonly icon: ReactNode;
  readonly active: boolean;
  readonly size?: TabHandleSize;
  /** Complication slot — a count pill or a dot. `count: 0` renders nothing. */
  readonly badge?: TabHandleBadge | null;
  /** Escape-hatch complication — an absolutely-positioned corner node
   *  (spinner, sync pip) for anything a badge can't express. */
  readonly overlay?: ReactNode;
}

/** The badge complication — the shared {@link Badge}, always corner-placed. */
function HandleBadge({ badge }: { badge: TabHandleBadge }): ReactNode {
  return "dot" in badge ? (
    <Badge dot tone={badge.dot} placement="corner" />
  ) : (
    <Badge count={badge.count} tone={badge.tone} placement="corner" />
  );
}

export const TabHandle = forwardRef<HTMLButtonElement, TabHandleProps>(
  function TabHandle(
    { label, icon, active, size = "sm", badge, overlay, className, disabled, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={active}
        aria-label={label}
        // Roving tabindex (ARIA tabs pattern): the strip is ONE tab stop —
        // the selected handle. The rest are reachable by ←/→ inside it.
        tabIndex={active ? 0 : -1}
        disabled={disabled}
        className={cx(
          "relative flex items-center justify-center transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500",
          "focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950",
          "disabled:cursor-not-allowed disabled:opacity-50",
          SIZE_CLASSES[size],
          active ? ACTIVE_CLASSES[size] : IDLE_CLASSES[size],
          className,
        )}
        {...rest}
      >
        {icon}
        {badge ? <HandleBadge badge={badge} /> : null}
        {overlay}
      </button>
    );
  },
);
